import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, MapPin, Phone, MessageCircle, Navigation, ArrowRight, Check } from "lucide-react";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { IMG } from "@/constants/images";

const AUDIENCE = ["Interior Designers", "Architects", "Furniture Stores", "Kitchen Studios", "Sofa Manufacturers", "Marble Suppliers", "Builders", "Home Decor"];

export default function Partners() {
  const [dealers, setDealers] = useState([]);
  const [city, setCity] = useState("");
  const [pincode, setPincode] = useState("");
  const [searched, setSearched] = useState(false);
  const navigate = useNavigate();

  const load = (params = {}) => api.get("/dealers", { params }).then(({ data }) => setDealers(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const search = () => { setSearched(true); load({ city: city || undefined, pincode: pincode || undefined }); };

  return (
    <div data-testid="partners-page">
      <Seo title="Find an ERRAH Partner" description="Find your nearest ERRAH surface protection partner by city or PIN code." />
      <section className="relative min-h-[46vh] flex items-center overflow-hidden">
        <img src={IMG.proj2} alt="ERRAH partners" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/40" />
        <div className="relative max-w-[1500px] mx-auto px-5 lg:px-10 w-full">
          <p className="errah-label mb-3">Partner Locator</p>
          <h1 className="serif-head text-4xl lg:text-6xl">Find your nearest ERRAH partner.</h1>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 max-w-2xl">
            <div className="relative flex-1"><MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
              <input value={city} onChange={(e) => setCity(e.target.value)} placeholder="City (e.g. Noida)" data-testid="dealer-city" className="w-full bg-black/60 border border-white/20 pl-9 pr-3 py-3 text-sm focus:border-[#C9A45C]" /></div>
            <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
              <input value={pincode} onChange={(e) => setPincode(e.target.value)} placeholder="PIN code" data-testid="dealer-pincode" className="w-full bg-black/60 border border-white/20 pl-9 pr-3 py-3 text-sm focus:border-[#C9A45C]" /></div>
            <button onClick={search} data-testid="dealer-search" className="bg-[#C9A45C] text-black px-7 py-3 text-sm font-semibold hover:bg-[#E4C987] transition-colors">SEARCH</button>
          </div>
        </div>
      </section>

      <section className="max-w-[1500px] mx-auto px-5 lg:px-10 py-12">
        <p className="errah-label mb-6">{searched ? `${dealers.length} partner(s) found` : "Our partners"}</p>
        {dealers.length === 0 ? (
          <div className="hair-border bg-warmblack p-12 text-center text-gray-500">No partners found for that search. Try another city, or book a consultation and we'll assign one.</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {dealers.map((d) => (
              <Reveal key={d.id}>
                <div className="hair-border bg-warmblack p-6" data-testid={`dealer-${d.id}`}>
                  <h3 className="serif-head text-2xl">{d.name}</h3>
                  <p className="text-sm text-gray-400 flex items-center gap-1 mt-1"><MapPin size={13} className="text-[#C9A45C]" /> {d.area}, {d.city}</p>
                  <div className="flex flex-wrap gap-1.5 mt-4">
                    {(d.services || []).map((s) => <span key={s} className="text-[11px] px-2 py-0.5 border border-[#C9A45C]/30 text-[#C9A45C]">{s}</span>)}
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-5">
                    <a href={`tel:${d.phone}`} className="flex flex-col items-center gap-1 py-2 border border-white/10 text-xs text-gray-300 hover:border-[#C9A45C] transition-colors"><Phone size={15} className="text-[#C9A45C]" /> Call</a>
                    <a href={`https://wa.me/${d.whatsapp}`} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-1 py-2 border border-white/10 text-xs text-gray-300 hover:border-[#C9A45C] transition-colors"><MessageCircle size={15} className="text-[#25D366]" /> WhatsApp</a>
                    <a href={d.maps_url || "#"} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-1 py-2 border border-white/10 text-xs text-gray-300 hover:border-[#C9A45C] transition-colors"><Navigation size={15} className="text-[#C9A45C]" /> Directions</a>
                  </div>
                  <button onClick={() => navigate("/consultation")} className="mt-3 w-full bg-[#C9A45C] text-black py-2.5 text-sm font-semibold hover:bg-[#E4C987] transition-colors">Book Consultation</button>
                </div>
              </Reveal>
            ))}
          </div>
        )}
      </section>

      {/* Become a partner */}
      <section className="bg-warmblack border-t border-[#C9A45C]/15">
        <div className="max-w-[1500px] mx-auto px-5 lg:px-10 py-14 grid lg:grid-cols-2 gap-10 items-center">
          <Reveal>
            <p className="errah-label mb-3">Become an ERRAH Partner</p>
            <h2 className="serif-head text-3xl lg:text-5xl">Build the future of surface protection with ERRAH.</h2>
            <div className="flex flex-wrap gap-2 mt-6">{AUDIENCE.map((a) => <span key={a} className="text-sm px-3 py-1.5 border border-white/15 text-gray-300">{a}</span>)}</div>
            <button onClick={() => navigate("/partner/login")} data-testid="become-partner" className="mt-7 inline-flex items-center gap-2 bg-[#C9A45C] text-black px-7 py-3.5 text-sm font-semibold hover:bg-[#E4C987] transition-colors rounded-sm">BECOME AN ERRAH PARTNER <ArrowRight size={15} /></button>
          </Reveal>
          <Reveal delay={0.1}>
            <ul className="space-y-3">
              {["Exclusive Territory", "Training & Certification", "Marketing Support", "Lead Generation", "Premium Products", "Dedicated Support"].map((b) => (
                <li key={b} className="flex items-center gap-3 text-gray-300 hair-border bg-[#080808] px-4 py-3"><Check size={17} className="text-[#C9A45C]" />{b}</li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
